import { getTableQuery } from "@/context/Table";
import useAuth from "@/hooks/useAuth";
import { TChangePasswordForm } from "@/modules/ChangePasswordForm";
import { TEditUserForm } from "@/modules/EditUserForm";
import { TNewUserForm } from "@/modules/NewUserForm";
import { TPersonalInformationForm } from "@/modules/PersonalInformationForm";
import { TTagForm } from "@/modules/TagForm";
import * as Sentry from "@sentry/react";
import axios, {
  AxiosError,
  AxiosResponse,
  RawAxiosRequestHeaders,
} from "axios";
import { components } from "swagger";

const API_URL = import.meta.env.VITE_APP_API_URL;

/**
 * Handles the auth bearer token captured when user is logged in and pass to axios instance.
 *
 * Token is captured from the useAuth hook.
 * @param axios
 *
 */
export function setupAPI(axios: any) {
  axios.defaults.headers.Accept = "application/json";
  axios.interceptors.request.use(
    (config: { headers: { Authorization: string } }) => {
      const auth = useAuth.getState().auth;
      if (auth && auth.token) {
        config.headers.Authorization = `Bearer ${auth.token}`;
      }

      return config;
    },
    (err: any) => Promise.reject(err)
  );

  axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      if (error.response) {
        // Skip 401 errors on sentry and redirect to login
        if (error.response.status === 401) {
          // Call force logout due to current token expired
          useAuth.getState().logout();
          return Promise.reject(error);
        }
        Sentry.captureException(error.response.data);
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        console.log(error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        Sentry.captureException(error.message);
        return Promise.reject(error);
      }

      Sentry.captureException(error);
      return Promise.reject(error);
    }
  );
  axios.interceptors;
}

// API PATHS
const USER_URL = `${API_URL}/users`;
const LOCATION_URL = `${API_URL}/locations`;
const CRATE_URL = `${API_URL}/crates`;
const ARTWORK_URL = `${API_URL}/artworks`;
const COLLECTOR_URL = `${API_URL}/collectors`;
const ARTIST_URL = `${API_URL}/artists`;
const TRANSACTION_GROUP_URL = `${API_URL}/transactiongroups`;
const TRANSACTION_GROUP_ITEM_URL = `${API_URL}/transactiongroupitems`;
const TAG_URL = `${API_URL}/tagassociates`;
const ASSOCIATES_URL = `${API_URL}/associates`;
const DISASSOCIATES_URL = `${API_URL}/disassociates`;
const JOBREFERENCES_URL = `${API_URL}/jobreferences`;
const STOCKTAKES_URL = `${API_URL}/stocktakes`;
const ASSETITEMS_URL = `${API_URL}/assetitems`;
const STOCKTAKELINEITEMS_URL = `${API_URL}/stocktakelineitems`;
const STOCKTAKEEVENTS_URL = `${API_URL}/stocktakeevents`;
const READ_URL = `${API_URL}/reads`;
const UNIT_OF_MEASUREMENT_URL = `${API_URL}/unitofmeasurements`;
const PACKING_METHODS_URL = `${API_URL}/packingmethods`;
const PRINTER_URL = `${import.meta.env.VITE_APP_PRINTER_URL}`;
const TRANSACTION_URL = `${API_URL}/transactions`;
const ROLE_URL = `${API_URL}/role`;
const TRANSACTION_TYPE_URL = `${API_URL}/transactiontypes`;
const DASHBOARD_URL = `${API_URL}/dashboards`;
const ALARM_URL = `${API_URL}/alarms`;
const STOCKOUT_URL = `${API_URL}/stockouts`;
const INNER_PACKAGING_URL = `${API_URL}/innerpackagings`;
const STOCKRETAKE_URL = `${API_URL}/stockretake`;
const LOGS_URL = `${API_URL}/logs`;
const TAGLOGS_URL = `${API_URL}/taglogs`;
const PERMISSIONSET_URL = `${API_URL}/permissionsets`;

const REPORT_URL = `${API_URL}/reports`;

export const getTransactionTypeById = async (
  id: string
): Promise<DefaultResponseWithData<TTransactionType>> => {
  return axios
    .get(`${TRANSACTION_TYPE_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TTransactionType>>) =>
        response.data
    );
};

export const getUserByToken = async (
  headers?: RawAxiosRequestHeaders
): Promise<DefaultPayloadResponse<TProfileResponse>> => {
  let request = headers
    ? axios.get(`${USER_URL}/profile`, {
        headers: headers,
      })
    : axios.get(`${USER_URL}/profile`);

  return request.then(
    (response: AxiosResponse<DefaultPayloadResponse<TProfileResponse>>) => {
      return response.data;
    }
  );
};

export const resetPasswordById = async (
  id: string
): Promise<DefaultPayloadResponse<TUserResetResponse>> => {
  return axios
    .put(`${USER_URL}/resetpassword/${id}`)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TUserResetResponse>>) =>
        response.data
    );
};

export const signInAdministrative = async (payload: {
  username: string;
  password: string;
}): Promise<DefaultResponse> => {
  return new Promise(async (resolve) => {
    // Authenticate the account
    let tokenVerification = await getAuthToken(payload);
    if (tokenVerification.success === false) {
      return resolve(tokenVerification);
    }

    if (!tokenVerification.payload || !tokenVerification.payload.token)
      return resolve({
        success: false,
        message: "Unable to authenticate the user",
      });

    // Validate the account type
    let getProfileResponse = await getUserByToken({
      Authorization: `Bearer ${tokenVerification.payload.token}`,
    });

    if (getProfileResponse.success === false)
      return resolve(getProfileResponse);

    if (
      !getProfileResponse.payload ||
      !getProfileResponse.payload.user ||
      !getProfileResponse.payload.user.accountType ||
      !getProfileResponse.payload.user.accountType.name
    )
      return resolve({
        success: false,
        message: "Unable to authenticate the user",
      });

    if (getProfileResponse.payload.user.accountType.name !== "Administrator")
      return resolve({
        success: false,
        message:
          "User dont have permission. Please make sure the account is an administrator",
      });

    return resolve({
      success: true,
      message: "User is authenticated and validated.",
    });
  });
};

export const getAuthToken = async (payload: {
  username: string;
  password: string;
}): Promise<DefaultPayloadResponse<AuthToken>> => {
  return axios
    .post(`${API_URL}/token`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<AuthToken>>) =>
        response.data
    );
};

const changeProfile = async (
  payload: TPersonalInformationForm
): Promise<DefaultResponse> => {
  return axios
    .put(`${USER_URL}/changeprofile`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const changePassword = async (
  payload: TChangePasswordForm
): Promise<DefaultResponse> => {
  return axios
    .put(`${USER_URL}/changepassword`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const getUsers = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TUser>> => {
  return axios
    .post(`${USER_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TUser>>) => response.data
    );
};

const createUser = async (
  payload: TNewUserForm
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${USER_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};

const editUser = async (
  id: string,
  payload: TEditUserForm
): Promise<DefaultResponse> => {
  return axios
    .put(`${USER_URL}/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const getUserById = async (
  id: string
): Promise<DefaultPayloadResponse<TUserView>> => {
  return axios
    .get(`${USER_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TUserView>>) =>
        response.data
    );
};

// const getAccountTypes = async (
//   payload: SieveRequestPayload
// ): Promise<DefaultSieveResponse<TRole>> => {
//   return axios
//     .post(`${API_URL}/accounttypes`, payload)
//     .then(
//       (response: AxiosResponse<DefaultSieveResponse<TRole>>) =>
//         response.data
//     );
// };

export const getAccountTypes = async (): Promise<
  DefaultPayloadResponse<TRole>
> => {
  return axios
    .get(`${USER_URL}/profile`)
    .then((response: AxiosResponse<DefaultPayloadResponse<TRole>>) => {
      return response.data;
    });
};
const getRoles = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TRoleResponse>> => {
  return axios
    .post(`${ROLE_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TRoleResponse>>) =>
        response.data
    );
};

const createRole = async (
  payload: components["schemas"]["CreateSubAccountTypePermissionsPostRequest"]
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${ROLE_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};
const editRole = async (payload: {
  id: number;
  addPermissions: string[];
  removePermissions: string[];
}): Promise<DefaultResponse> => {
  return axios
    .put(`${ROLE_URL}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};
// export const login = async (payload: {
//   email: string;
//   password: string;
// }): Promise<{token: AuthToken, user:AuthUser}> =>
//   new Promise(async (resolve, reject) => {
//     let tokenResponse = await getAuthToken(payload);

//     if (tokenResponse.status !== "success") {
//       resolve()
//     };

//     resolve({
//       token: tokenResponse.data
//     });
//   });

// Read API

const getAsssetItemRead = async (payload: { codes: string[] }) => {
  return axios
    .post(`${READ_URL}/assetitems`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TTagReadResponse>>) =>
        response.data
    );
};

// Locations API

const getLocations = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TLocation>> => {
  return axios
    .post(`${LOCATION_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TLocation>>) =>
        response.data
    );
};

const getLocationById = async (
  id: string
): Promise<DefaultResponseWithData<TLocation>> => {
  return axios
    .get(`${LOCATION_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TLocation>>) =>
        response.data
    );
};

const deleteLocation = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${LOCATION_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

// Crates API

const getCrates = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TCrate>> => {
  return axios
    .post(`${CRATE_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TCrate>>) => response.data
    );
};

const getCrateById = async (
  id: string
): Promise<DefaultResponseWithData<TCrate>> => {
  return axios
    .get(`${CRATE_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TCrate>>) =>
        response.data
    );
};

const createCrate = async (
  payload: TCrate
): Promise<DefaultPayloadResponse<string>> => {
  return axios
    .post(`${CRATE_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<string>>) => response.data
    );
};

const createCrateWithValidation = async (
  payload: TCrate
): Promise<DefaultPayloadResponse<string>> =>
  new Promise(async (resolve) => {
    let validateBarcodeResponse = await getAsssetItemRead({
      codes: [payload?.crateBarcode ?? ""],
    });

    if (
      validateBarcodeResponse.success === false ||
      !validateBarcodeResponse.payload
    ) {
      return resolve({
        success: false,
        message: "Invalid Barcode",
      });
    }

    if (
      validateBarcodeResponse.payload.artworkTags === null ||
      validateBarcodeResponse.payload.artworkTags === undefined ||
      validateBarcodeResponse.payload.crateTags === null ||
      validateBarcodeResponse.payload.crateTags === undefined ||
      validateBarcodeResponse.payload.newTags === null ||
      validateBarcodeResponse.payload.newTags === undefined ||
      validateBarcodeResponse.payload.innerPackagingTags === null ||
      validateBarcodeResponse.payload.innerPackagingTags === undefined
    ) {
      return resolve({
        success: false,
        message: "Invalid Barcode",
      });
    }

    if (validateBarcodeResponse.payload.newTags.length === 0) {
      return resolve({
        success: false,
        message: "Invalid Barcode",
      });
    }

    let createCrateResponse = await createCrate(payload);

    if (createCrateResponse.success === false) {
      return resolve(createCrateResponse);
    }

    return resolve(createCrateResponse);
  });

const updateCrate = async (
  id: string,
  payload: TCrate
): Promise<DefaultResponse> => {
  return axios
    .put(`${CRATE_URL}/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const deleteCrate = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${CRATE_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

// Inner Packaging API

const getInnerPackagings = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TInnerPackagingList>> => {
  return axios
    .post(`${INNER_PACKAGING_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TInnerPackagingList>>) =>
        response.data
    );
};

const getInnerPackagingById = async (
  id: string
): Promise<DefaultResponseWithData<TInnerPackaging>> => {
  return axios
    .get(`${INNER_PACKAGING_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TInnerPackaging>>) =>
        response.data
    );
};

const createInnerPackaging = async (
  payload: TInnerPackaging
): Promise<DefaultResponse> => {
  return axios
    .post(`${INNER_PACKAGING_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<DefaultResponse>>) =>
        response.data
    );
};

const createInnerPackagingWithValidation = async (
  payload: TInnerPackaging
): Promise<DefaultPayloadResponse<string>> =>
  new Promise(async (resolve) => {
    let validateBarcodeResponse = await getAsssetItemRead({
      codes: [payload?.barcode ?? ""],
    });

    if (
      validateBarcodeResponse.success === false ||
      !validateBarcodeResponse.payload
    ) {
      return resolve({
        success: false,
        message: "Invalid Barcode",
      });
    }

    if (
      validateBarcodeResponse.payload.artworkTags === null ||
      validateBarcodeResponse.payload.artworkTags === undefined ||
      validateBarcodeResponse.payload.crateTags === null ||
      validateBarcodeResponse.payload.crateTags === undefined ||
      validateBarcodeResponse.payload.innerPackagingTags === null ||
      validateBarcodeResponse.payload.innerPackagingTags === undefined ||
      validateBarcodeResponse.payload.newTags === null ||
      validateBarcodeResponse.payload.newTags === undefined
    ) {
      return resolve({
        success: false,
        message: "Invalid Barcode",
      });
    }

    if (validateBarcodeResponse.payload.newTags.length === 0) {
      return resolve({
        success: false,
        message: "Invalid Barcode",
      });
    }

    let createInnerPackagingResponse = await createInnerPackaging(payload);

    if (createInnerPackagingResponse.success === false) {
      return resolve(createInnerPackagingResponse);
    }

    return resolve(createInnerPackagingResponse);
  });

const updateInnerPackaging = async (
  id: string,
  payload: TInnerPackaging
): Promise<DefaultResponse> => {
  return axios
    .put(`${INNER_PACKAGING_URL}/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const deleteInnerPackaging = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${INNER_PACKAGING_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

// Artwork API

const getArtworks = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TArtwork>> => {
  return axios
    .post(`${ARTWORK_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TArtwork>>) => response.data
    );
};

const getArtworkById = async (
  id: string
): Promise<DefaultResponseWithData<TArtwork>> => {
  return axios
    .get(`${ARTWORK_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TArtwork>>) =>
        response.data
    );
};

const createArtwork = async (payload: TArtwork): Promise<DefaultResponse> => {
  return axios
    .post(`${ARTWORK_URL}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const createArtworkWithValidation = async (
  payload: TArtwork
): Promise<DefaultResponse> =>
  new Promise(async (resolve) => {
    let validateBarcodeResponse = await getAsssetItemRead({
      codes: [payload?.barcodeId ?? ""],
    });

    if (
      validateBarcodeResponse.success === false ||
      !validateBarcodeResponse.payload
    ) {
      return resolve({
        success: false,
        message: "Invalid Barcode",
      });
    }

    if (
      validateBarcodeResponse.payload.artworkTags === null ||
      validateBarcodeResponse.payload.artworkTags === undefined ||
      validateBarcodeResponse.payload.crateTags === null ||
      validateBarcodeResponse.payload.crateTags === undefined ||
      validateBarcodeResponse.payload.newTags === null ||
      validateBarcodeResponse.payload.newTags === undefined ||
      validateBarcodeResponse.payload.innerPackagingTags === null ||
      validateBarcodeResponse.payload.innerPackagingTags === undefined
    ) {
      return resolve({
        success: false,
        message: "Invalid Barcode",
      });
    }

    if (validateBarcodeResponse.payload.newTags.length === 0) {
      return resolve({
        success: false,
        message: "Invalid Barcode",
      });
    }

    let createArtworkResponse = await createArtwork(payload);

    if (createArtworkResponse.success === false) {
      return resolve(createArtworkResponse);
    }

    return resolve(createArtworkResponse);
  });

const updateArtwork = async (
  id: string,
  payload: TArtwork
): Promise<DefaultResponse> => {
  return axios
    .put(`${ARTWORK_URL}/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const deleteArtwork = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${ARTWORK_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

// Collectors API

const getCollectors = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TCollector>> => {
  return axios
    .post(`${COLLECTOR_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TCollector>>) =>
        response.data
    );
};

const deleteCollector = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${COLLECTOR_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

// Artists API

const getArtists = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TArtist>> => {
  return axios
    .post(`${ARTIST_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TArtist>>) => response.data
    );
};

const deleteArtist = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${ARTIST_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

// TransactionGroup API

const getTranasctionGroups = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TTransactionGroup>> => {
  return axios
    .post(`${TRANSACTION_GROUP_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TTransactionGroup>>) =>
        response.data
    );
};

const getTransactionGroupById = async (
  id: string
): Promise<DefaultResponseWithData<TTransactionGroup>> => {
  return axios
    .get(`${TRANSACTION_GROUP_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TTransactionGroup>>) =>
        response.data
    );
};

// TransactionGroupItem API

const getTranasctionGroupItems = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TTransactionGroupItem>> => {
  return axios
    .post(`${TRANSACTION_GROUP_ITEM_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TTransactionGroupItem>>) =>
        response.data
    );
};
const getAllTranasctionGroupItems = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TTransactionGroupItem>> => {
  return axios
    .post(`${TRANSACTION_GROUP_ITEM_URL}/all`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TTransactionGroupItem>>) =>
        response.data
    );
};

// Measurement API
const getUnitOfMeasures = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TUnitOfMeasurement>> => {
  return axios
    .post(`${UNIT_OF_MEASUREMENT_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TUnitOfMeasurement>>) =>
        response.data
    );
};

// StockIn
const stockIn = async (
  payload: components["schemas"]["StockInRequest"]
): Promise<SyncResponse> => {
  return axios
    .post(`${API_URL}/stockins`, payload)
    .then((response: AxiosResponse<SyncResponse>) => response.data);
};

// Picklist
const getPicklist = async (
  id: string
): Promise<DefaultResponseWithData<TPicklist>> => {
  return Promise.allSettled([
    getTransactionGroupById(id),
    getTranasctionGroupItems(
      getTableQuery({
        filters: [
          {
            open: false,
            id: 1,
            key: "transactionGroupId",
            title: "TransactionGroupId",
            kind: "built-in",
            compare: "is",
            type: "guid",
            value: id,
          },
        ],
        sorts: [],
        page: 0,
        pageSize: 1000,
        search: "",
      })
    ),
  ]).then(
    (
      responses: [
        PromiseSettledResult<DefaultResponseWithData<TTransactionGroup>>,
        PromiseSettledResult<DefaultSieveResponse<TTransactionGroupItem>>
      ]
    ) => {
      const [transactionGroupResponse, transactionGroupItemResponse] =
        responses;

      if (
        transactionGroupResponse.status === "rejected" ||
        transactionGroupItemResponse.status === "rejected"
      ) {
        return {
          success: false,
          message: "Something went wrong while fetching data.",
        };
      }

      if (
        transactionGroupResponse.value.success === false ||
        transactionGroupItemResponse.value.success === false
      ) {
        return {
          success: false,
          message: "Something went wrong while fetching data.",
        };
      }

      if (!transactionGroupResponse.value.payload) {
        return {
          success: false,
          message: "Something went wrong while fetching data.",
        };
      }

      return {
        success: true,
        message: "Picklist fetched",
        payload: {
          data: {
            ...transactionGroupResponse.value.payload.data,
            items: transactionGroupItemResponse.value.payload,
          },
        },
      };
    }
  );
};

//#region Tags API

const getTags = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TTag>> => {
  return axios
    .post(`${TAG_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TTag>>) => response.data
    );
};

const deleteTag = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${TAG_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

//#endregion Tags

//#region Associates API
const printTags = async (
  payload: TTagForm
): Promise<DefaultPayloadResponse<PrintTagsResponse>> => {
  return axios
    .post(`${API_URL}/print`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<PrintTagsResponse>>) =>
        response.data
    );
};

const reprintTags = async (
  payload: TTagReprintRequest
): Promise<DefaultPayloadResponse<PrintTagsResponse>> => {
  return axios
    .post(`${API_URL}/reprint`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<PrintTagsResponse>>) =>
        response.data
    );
};

const printAssetItem = async (
  payload: TAssetItemPrintRequest
): Promise<DefaultPayloadResponse<PrintTagsResponse>> => {
  return axios
    .post(`${API_URL}/print/assetitem`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<PrintTagsResponse>>) =>
        response.data
    );
};

const barcodeCheck = async (
  payload: TBarcodeCheckRequest
): Promise<DefaultPayloadResponse<TBarcodeCheckResponse>> => {
  return axios
    .post(`${API_URL}/barcodechecker`, payload)
    .then(
      (
        response: AxiosResponse<DefaultPayloadResponse<TBarcodeCheckResponse>>
      ) => response.data
    );
};

//#endregion Associates

//#region JobReferences API
const getJobReferences = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TJobReferences>> => {
  return axios
    .post(`${JOBREFERENCES_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TJobReferences>>) =>
        response.data
    );
};
//#endregion JobReferences

//#region Packing Method API
const getPackingMethods = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TPackingMethodList>> => {
  return axios
    .post(`${PACKING_METHODS_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TPackingMethodList>>) =>
        response.data
    );
};

//#endregion Packing Methond API

//#regrion StockRetake API
const completeStockRetake = async (
  payload: TStockRetakeRequest
): Promise<DefaultResponse> => {
  return axios
    .post(`${STOCKRETAKE_URL}/complete`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

//#endregion StockRetake API

//#region StockTake API
const getStockTake = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TStockTake>> => {
  return axios
    .post(`${STOCKTAKES_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TStockTake>>) =>
        response.data
    );
};

const getStockTakeById = async (
  id: string
): Promise<DefaultResponseWithData<TStockTake>> => {
  return axios
    .get(`${STOCKTAKES_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TStockTake>>) =>
        response.data
    );
};

const createStockTake = async (
  payload: TNewStockTake
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${STOCKTAKES_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};

const checkStockTake = async (
  payload: TCheckStockTake
): Promise<DefaultPayloadResponse<TStockVerificationCheckResponse>> => {
  return axios
    .post(`${STOCKTAKES_URL}/check`, payload)
    .then(
      (
        response: AxiosResponse<
          DefaultPayloadResponse<TStockVerificationCheckResponse>
        >
      ) => response.data
    );
};

const completeStockTake = async (
  payload: TCompleteStockTake
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${STOCKTAKES_URL}/complete`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};
//#endregion StockTake

//#region AssetItem API
const getAssetItem = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TAssetItem>> => {
  return axios
    .post(`${ASSETITEMS_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TAssetItem>>) =>
        response.data
    );
};

const getAssetItemById = async (
  id: string
): Promise<DefaultResponseWithData<TAssetItem>> => {
  return axios
    .get(`${ASSETITEMS_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TAssetItem>>) =>
        response.data
    );
};

const associateAssetItemToCrate = async (
  payload: AssetItemToCrateRequest
): Promise<DefaultPayloadResponse<AssetITemToCratePayload>> => {
  return axios
    .post(`${ASSOCIATES_URL}/assetitemtocrate`, payload)
    .then(
      (
        response: AxiosResponse<DefaultPayloadResponse<AssetITemToCratePayload>>
      ) => response.data
    );
};

const diassociateAssetItemFromCrate = async (
  payload: AssetItemToCrateRequest
): Promise<DefaultPayloadResponse<AssetITemToCratePayload>> => {
  return axios
    .post(`${DISASSOCIATES_URL}/assetitemfromcrate`, payload)
    .then(
      (
        response: AxiosResponse<DefaultPayloadResponse<AssetITemToCratePayload>>
      ) => response.data
    );
};

//#endregion AssetItem

//#region StockTakeLineItem API
const getStockTakeLineItem = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TStockTakeLineItem>> => {
  // payload.filters = payload.filters? `filter@=none | ${payload.filters}` : "filter@=none";
  return axios
    .post(`${STOCKTAKELINEITEMS_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TStockTakeLineItem>>) =>
        response.data
    );
};
//#endregion StockTakeLineItem

//#region StockTakeEvent API
const getStockTakeEvent = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TStockTakeEvent>> => {
  return axios
    .post(`${STOCKTAKEEVENTS_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TStockTakeEvent>>) =>
        response.data
    );
};
//#endregion StockTakeEvent

//#region StockVerification API
const getStockVerification = async (
  id: string
): Promise<DefaultResponseWithData<TStockVerification>> => {
  return Promise.allSettled([
    getStockTakeById(id),
    getStockTakeLineItem(
      getTableQuery({
        filters: [
          {
            open: false,
            id: 1,
            key: "stockTakeId",
            title: "stockTakeId",
            kind: "built-in",
            compare: "is",
            type: "guid",
            value: id,
          },
        ],
        sorts: [],
        page: 0,
        pageSize: 1000,
        search: "",
      })
    ),
  ]).then(
    (
      responses: [
        PromiseSettledResult<DefaultResponseWithData<TStockTake>>,
        PromiseSettledResult<DefaultSieveResponse<TStockTakeLineItem>>
      ]
    ) => {
      const [stockTakeResponse, stockTakeLineItemResponse] = responses;

      if (
        stockTakeResponse.status === "rejected" ||
        stockTakeLineItemResponse.status === "rejected"
      ) {
        return {
          success: false,
          message: "Something went wrong while fetching data.",
        };
      }

      if (stockTakeResponse.value.success === false) {
        return {
          success: false,
          message: "Something went wrong while fetching data.",
        };
      }

      if (!stockTakeResponse.value.payload) {
        return {
          success: false,
          message: "Something went wrong while fetching data.",
        };
      }

      return {
        success: true,
        message: "Stock verification fetched",
        payload: {
          data: {
            ...stockTakeResponse.value.payload.data,
            items: stockTakeLineItemResponse.value.payload,
          },
        },
      };
    }
  );
};
//#endregion StockVerificaton

const sendToPrinter = (
  request: PrinterPayload
): Promise<PrinterServerResponse> => {
  return axios
    .post(`${PRINTER_URL}`, { ...request })
    .then((response: AxiosResponse<PrinterServerResponse>) => response.data);
};

const associateArtworkToCrate = (request: {
  crateId: string;
  artworkIds: string[];
}): Promise<DefaultPayloadResponse<TArtworkToCrateResponse>> => {
  return axios
    .post(`${ASSOCIATES_URL}/artworktocrate`, request)
    .then(
      (
        response: AxiosResponse<DefaultPayloadResponse<TArtworkToCrateResponse>>
      ) => response.data
    );
};

const associateArtworkToInnerPackaging = (request: {
  innerPackagingId: string;
  artworkIds: string[];
}): Promise<DefaultPayloadResponse<TArtworkToInnerPackagingResponse>> => {
  return axios
    .post(`${ASSOCIATES_URL}/artworktoinnerpackaging`, request)
    .then(
      (
        response: AxiosResponse<
          DefaultPayloadResponse<TArtworkToInnerPackagingResponse>
        >
      ) => response.data
    );
};

const associateInnerPackagingToCrate = (request: {
  createId: string;
  innerPackagingIds: string[];
}): Promise<DefaultPayloadResponse<TInnerPackagingToCrateResponse>> => {
  return axios
    .post(`${ASSOCIATES_URL}/innerpackagingtocrate`, request)
    .then(
      (
        response: AxiosResponse<
          DefaultPayloadResponse<TInnerPackagingToCrateResponse>
        >
      ) => response.data
    );
};

const diassociateArtworkToInnerPackaging = (request: {
  innerPackagingId: string;
  artworkIds: string[];
}): Promise<DefaultPayloadResponse<TArtworkToInnerPackagingResponse>> => {
  return axios
    .post(`${DISASSOCIATES_URL}/artworkfrominnerpackaging`, request)
    .then(
      (
        response: AxiosResponse<
          DefaultPayloadResponse<TArtworkToInnerPackagingResponse>
        >
      ) => response.data
    );
};

const diassociateInnerPackagingToCrate = (request: {
  createId: string;
  innerPackagingIds: string[];
}): Promise<DefaultPayloadResponse<TInnerPackagingToCrateResponse>> => {
  return axios
    .post(`${DISASSOCIATES_URL}/innerpackagingfromcrate`, request)
    .then(
      (
        response: AxiosResponse<
          DefaultPayloadResponse<TInnerPackagingToCrateResponse>
        >
      ) => response.data
    );
};

const diassociateArtworkToCrate = (request: {
  crateId: string;
  artworkIds: string[];
}): Promise<DefaultPayloadResponse<TArtworkToCrateResponse>> => {
  return axios
    .post(`${DISASSOCIATES_URL}/artworkfromcrate`, request)
    .then(
      (
        response: AxiosResponse<DefaultPayloadResponse<TArtworkToCrateResponse>>
      ) => response.data
    );
};

//#region Transactions API
const getTransaction = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TTransaction>> => {
  return axios
    .post(`${TRANSACTION_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TTransaction>>) =>
        response.data
    );
};
//#endregion Transactions

//#region Alarm API
const getAlarm = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TAlarm>> => {
  return axios
    .post(`${ALARM_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TAlarm>>) => response.data
    );
};
//#endregion Alarm

//#region Dashboard API
const getDashboardData = async (): Promise<IntervalStatisticResponse> => {
  return axios
    .get(`${DASHBOARD_URL}`)
    .then(
      (response: AxiosResponse<IntervalStatisticResponse>) => response.data
    );
};

const getCollectorMetrics = async (): Promise<TCollectorMetrics> => {
  return axios
    .get(`${DASHBOARD_URL}/collectormetrics`)
    .then((response: AxiosResponse<TCollectorMetrics>) => response.data);
};

const getStockInMetrics = async (): Promise<TStockInMetricsResponse> => {
  return axios
    .get(`${DASHBOARD_URL}/stockinmetrics`)
    .then((response: AxiosResponse<TStockInMetricsResponse>) => response.data);
};
const getStockOutMetrics = async (): Promise<TStockOutMetricsResponse> => {
  return axios
    .get(`${DASHBOARD_URL}/stockoutmetrics`)
    .then((response: AxiosResponse<TStockOutMetricsResponse>) => response.data);
};
const getShipmentMetrics = async (): Promise<TShipmentMetrics> => {
  return axios
    .get(`${DASHBOARD_URL}/shipmentmetrics`)
    .then((response: AxiosResponse<TShipmentMetrics>) => response.data);
};
const getAlarmMetrics = async (): Promise<TAlarmMetrics> => {
  return axios
    .get(`${DASHBOARD_URL}/alarmmetrics`)
    .then((response: AxiosResponse<TAlarmMetrics>) => response.data);
};
const getArtworkCreationMetrics = async (
  payload: TArtworkCreationMetricsRequest
): Promise<TArtworkCreationMetricsResponse> => {
  return axios
    .post(`${DASHBOARD_URL}/artworkcreationmetrics`, payload)
    .then(
      (response: AxiosResponse<TArtworkCreationMetricsResponse>) =>
        response.data
    );
};
const getTagMetrics = async (): Promise<TTagMetrics> => {
  return axios
    .get(`${DASHBOARD_URL}/tagmetrics`)
    .then((response: AxiosResponse<TTagMetrics>) => response.data);
};
//#endregion Dashboard

//#region StockOut
const cancelPicklist = async (
  payload: TStockOutCancelRequest
): Promise<TStockOutCancelResponse> => {
  return axios
    .post(`${STOCKOUT_URL}/cancel`, payload)
    .then((response: AxiosResponse<TStockOutCancelResponse>) => response.data);
};
//#endregion StockOut

//#region Logs API
const getLogs = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TLogs>> => {
  return axios
    .post(`${LOGS_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TLogs>>) => response.data
    );
};
const getTagLogs = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TTagLogs>> => {
  return axios
    .post(`${TAGLOGS_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TTagLogs>>) => response.data
    );
};
//#endregion Logs API
//#region Permissionsets API
const getPermissionSets = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TPermissionSets>> => {
  return axios
    .post(`${PERMISSIONSET_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TPermissionSets>>) =>
        response.data
    );
};
//#endregion Permissionsets API

const getDailyTransactionView = async (
  payload: TDateRangeSievePayload
): Promise<DefaultSieveResponse<TDailyTripReportList>> => {
  return axios
    .post(`${REPORT_URL}/dailytransactiongroupreportview`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TDailyTripReportList>>) =>
        response.data
    );
};

export default {
  getAuthToken,
  getUserByToken,
  getLocations,
  getLocationById,
  deleteLocation,
  getCrates,
  updateCrate,
  createCrate,
  getCrateById,
  deleteCrate,
  getArtworks,
  getArtworkById,
  createArtwork,
  updateArtwork,
  deleteArtwork,
  getCollectors,
  deleteCollector,
  getArtists,
  deleteArtist,
  getAsssetItemRead,
  getTranasctionGroups,
  getTransactionGroupById,
  getTranasctionGroupItems,
  getPicklist,
  getTags,
  deleteTag,
  printTags,
  reprintTags,
  getJobReferences,
  getUnitOfMeasures,
  getPackingMethods,
  getStockTake,
  getAssetItem,
  getStockTakeLineItem,
  createStockTake,
  getStockTakeEvent,
  checkStockTake,
  completeStockTake,
  getStockTakeById,
  getStockVerification,
  sendToPrinter,
  associateArtwork: associateArtworkToCrate,
  diassociateArtwork: diassociateArtworkToCrate,
  getTransaction,
  createCrateWithValidation,
  createArtworkWithValidation,
  stockIn,
  getTransactionTypeById,
  changeProfile,
  changePassword,
  getUsers,
  createUser,
  getAccountTypes,
  getRoles,
  createRole,
  editRole,
  editUser,
  getUserById,
  printAssetItem,
  getDashboardData,
  getAlarm,
  signInAdministrative,
  getCollectorMetrics,
  getStockInMetrics,
  getStockOutMetrics,
  getShipmentMetrics,
  getAlarmMetrics,
  getArtworkCreationMetrics,
  cancelPicklist,
  barcodeCheck,
  getInnerPackagings,
  getInnerPackagingById,
  createInnerPackaging,
  updateInnerPackaging,
  deleteInnerPackaging,
  associateArtworkToInnerPackaging,
  associateInnerPackagingToCrate,
  diassociateArtworkToInnerPackaging,
  diassociateInnerPackagingToCrate,
  createInnerPackagingWithValidation,
  completeStockRetake,
  associateAssetItemToCrate,
  diassociateAssetItemFromCrate,
  getAssetItemById,
  getLogs,
  getTagMetrics,
  getTagLogs,
  getPermissionSets,
  getDailyTransactionView,
  getAllTranasctionGroupItems,
};
